import React, { useEffect, useMemo, useRef, useState } from "react";
import { client, inServiceWindow } from "@/lib/api";
import { EquipmentTickList } from "@/components/EquipmentTickList";
import { equipmentParts, equipmentSummary } from "@/lib/equipment";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { AlertTriangle, Timer } from "lucide-react";

export default function Intake() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [areas, setAreas] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [sla, setSla] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [priority, setPriority] = useState("standard");
  const [jobType, setJobType] = useState("repair");
  const [description, setDescription] = useState("");
  const [reportedBy, setReportedBy] = useState("");
  const [slaHours, setSlaHours] = useState("");
  const [sched, setSched] = useState("");
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const bulkKey = useRef(null);
  const isMaintenance = ["scheduled_pm", "contract"].includes(jobType);
  const [startTs] = useState(() => Date.now());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    (async () => {
      const [c, s, a, e] = await Promise.all([
        client.get("/customers"), client.get("/sites"),
        client.get("/areas"), client.get("/equipment"),
      ]);
      setCustomers(c.data); setSites(s.data); setAreas(a.data); setEquipment(e.data);
      try { setSla((await client.get("/settings/sla")).data); } catch (error) { console.warn("SLA settings unavailable, using defaults", error); }
    })();
  }, []);
  useEffect(() => { const i = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(i); }, []);

  const filteredSites = useMemo(() => sites.filter(s => s.customer_id === customerId), [sites, customerId]);
  const filteredAreas = useMemo(() => areas.filter(a => a.site_id === siteId), [areas, siteId]);
  const filteredEq    = useMemo(() => equipment.filter(e => e.area_id === areaId), [equipment, areaId]);
  const selectedArea  = areas.find(a => a.id === areaId);

  useEffect(() => { if (!filteredSites.find(s => s.id === siteId)) setSiteId(""); }, [customerId]);
  useEffect(() => { if (!filteredAreas.find(a => a.id === areaId)) setAreaId(""); }, [siteId]);
  useEffect(() => { if (!filteredEq.find(e => e.id === equipmentId)) setEquipmentId(""); }, [areaId]);

  useEffect(() => {
    // auto-fill reporter from site
    const s = sites.find(x => x.id === siteId);
    if (s?.on_site_contact && !reportedBy) setReportedBy(s.on_site_contact);
  }, [siteId]);

  const elapsed = Math.floor((Date.now() - startTs) / 1000);
  const warnTime = elapsed > 60;

  const windowOk = useMemo(() => sched ? inServiceWindow(selectedArea, new Date(sched).toISOString()) : true, [sched, selectedArea]);

  const activeTargets = (sla && (sla.peak_active_now ? sla.peak_sla_targets : sla.sla_targets)) || { equipment_down: 4, standard: 24, low: 72 };

  const submit = async () => {
    if (isMaintenance) return submitBulk();
    if (!customerId || !siteId || !areaId || !description.trim()) {
      toast.error("Fill customer, site, area and a short description.");
      return;
    }
    const payload = {
      customer_id: customerId, site_id: siteId, area_id: areaId,
      equipment_id: equipmentId || null, priority, job_type: jobType, description,
      reported_by: reportedBy,
      status: sched ? "scheduled" : "awaiting_dispatch",
      scheduled_at: sched ? new Date(sched).toISOString() : null,
    };
    if (slaHours) payload.sla_hours = Number(slaHours);
    try {
      const r = await client.post("/jobs", payload);
      toast.success(`Ticket ${r.data.ticket_number} created`);
      navigate("/dispatch");
    } catch (e) {
      toast.error("Create failed");
    }
  };

  const submitBulk = async () => {
    if (!customerId || !siteId || !selectedUnits.length || !description.trim()) {
      toast.error("Fill customer, site, at least one piece of equipment and a short description.");
      return;
    }
    if (submitting) return;
    if (!bulkKey.current) bulkKey.current = `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      setSubmitting(true);
      const r = await client.post("/jobs/bulk", {
        idempotency_key: bulkKey.current, customer_id: customerId, site_id: siteId,
        equipment_ids: selectedUnits, priority, job_type: jobType, description,
        scheduled_at: sched ? new Date(sched).toISOString() : null,
      });
      bulkKey.current = null;
      setBulkResult(r.data);
      setSelectedUnits([]);
      toast.success(r.data.jobs?.[0]?.visit_ticket
        ? `1 ticket created for ${r.data.unit_count} unit${r.data.unit_count === 1 ? "" : "s"}`
        : `${r.data.count} maintenance jobs created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  const siteAreas = useMemo(() => areas.filter(a => a.site_id === siteId), [areas, siteId]);
  const siteEquipment = useMemo(() => {
    const ids = new Set(siteAreas.map(a => a.id));
    return equipment.filter(e => ids.has(e.area_id));
  }, [equipment, siteAreas]);
  useEffect(() => { setSelectedUnits([]); }, [siteId]);

  return (
    <div data-testid="intake-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Call-Taker</div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>New Service Request</h1>
          <div className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>Target: a logged ticket in under 60 seconds.</div>
          {sla?.peak_active_now && (
            <div className="mt-2 bf-chip"
              style={{ background: "#F2EEFE", color: "#5B21B6", padding: "6px 12px", fontSize: "12px" }}
              data-testid="intake-peak-badge">
              <span className="bf-dot" style={{ background: "#5B21B6" }} />
              Peak season — tighter SLA targets in effect
            </div>
          )}
        </div>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-[999px] mono text-[13px]`}
          style={{ background: warnTime ? "#FFF6E5" : "#EEF1F5", color: warnTime ? "#96650A" : "#5B6472" }}
          data-testid="intake-timer"
        >
          <Timer size={14} /> {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
          <span className="text-[11px] uppercase tracking-wider">elapsed</span>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
        {bulkResult && (
          <div className="bf-card lg:col-span-2" style={{ padding: "16px 20px", background: "#EAF7EF" }} data-testid="bulk-result">
            <div className="text-[15px] font-semibold" style={{ color: "#136B36" }} data-testid="bulk-result-title">
              {bulkResult.unit_count && bulkResult.count === 1 && bulkResult.jobs[0]?.visit_ticket
                ? `1 ticket created for ${bulkResult.unit_count} unit${bulkResult.unit_count === 1 ? "" : "s"}`
                : `${bulkResult.count} maintenance jobs created`}
            </div>
            <ul className="mt-2 text-[13px] space-y-0.5">
              {bulkResult.jobs.map(j => (
                <li key={j.id} data-testid={`bulk-result-${j.ticket_number}`}>
                  <span className="mono">{j.ticket_number}</span> — {j.visit_ticket ? j.equipment_label : equipmentSummary(equipment.find(e => e.id === j.equipment_id) || { type: j.equipment_type, serial_number: j.serial_number })}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="h-9 mt-3 bf-btn-ghost" onClick={() => navigate("/dispatch")} data-testid="bulk-result-dispatch">Go to dispatch</Button>
          </div>
        )}
        <div className="bf-card" style={{ padding: "20px" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Customer" required>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Site" required>
              <Select value={siteId} onValueChange={setSiteId} disabled={!customerId}>
                <SelectTrigger data-testid="site-select"><SelectValue placeholder={customerId ? "Select site" : "Pick a customer first"} /></SelectTrigger>
                <SelectContent>{filteredSites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {!isMaintenance && (
            <Field label="Area" required>
              <Select value={areaId} onValueChange={setAreaId} disabled={!siteId}>
                <SelectTrigger data-testid="area-select"><SelectValue placeholder={siteId ? "Select area" : "Pick a site first"} /></SelectTrigger>
                <SelectContent>{filteredAreas.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            )}
            {isMaintenance ? (
              <div className="md:col-span-2">
                <Field label="Equipment (tick each unit to service)" required>
                  {siteId ? (
                    <EquipmentTickList areas={siteAreas} equipment={siteEquipment}
                                       selected={selectedUnits} setSelected={setSelectedUnits} />
                  ) : (
                    <div className="text-[12.5px]" style={{ color: "#8A93A1" }}>Pick a site first</div>
                  )}
                </Field>
              </div>
            ) : (
            <Field label="Equipment">
              <Select value={equipmentId} onValueChange={setEquipmentId} disabled={!areaId}>
                <SelectTrigger data-testid="equipment-select"><SelectValue placeholder={areaId ? "Optional" : "Pick an area first"} /></SelectTrigger>
                <SelectContent>{filteredEq.map(e => <SelectItem key={e.id} value={e.id} data-testid={`equipment-option-${e.id}`}>{equipmentSummary(e)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            )}
            <Field label="Priority">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipment_down">Equipment down ({activeTargets.equipment_down}h)</SelectItem>
                  <SelectItem value="standard">Standard ({activeTargets.standard}h)</SelectItem>
                  <SelectItem value="low">Low ({activeTargets.low}h)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Job type">
              <Select value={jobType} onValueChange={setJobType}>
                <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="scheduled_pm">Scheduled PM</SelectItem>
                  <SelectItem value="warranty">Warranty</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="quoted_work">Quoted work</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="SLA override (hours)">
              <Input type="number" placeholder="auto" value={slaHours} onChange={e => setSlaHours(e.target.value)} data-testid="sla-input" />
            </Field>
            <Field label="Reported by">
              <Input value={reportedBy} onChange={e => setReportedBy(e.target.value)} data-testid="reporter-input" />
            </Field>
            <Field label="Schedule now (optional)">
              <Input type="datetime-local" value={sched} onChange={e => setSched(e.target.value)} data-testid="schedule-input" />
              {sched && new Date(sched) < new Date() && (
                <div className="mt-1.5 flex items-center gap-1.5 text-[12.5px]" style={{ color: "#96650A" }}
                  data-testid="schedule-past-warning">
                  <AlertTriangle size={13} color="#96650A" /> This date is in the past.
                </div>
              )}
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Problem description" required>
              <Textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Oven not reaching set temperature; chef reports slow preheat on breakfast line."
                rows={3} data-testid="description-input"
              />
            </Field>
          </div>

          {sched && selectedArea && !windowOk && (
            <div className="mt-4 flex items-start gap-2 p-3.5 rounded-[14px]"
              style={{ background: "#FFF6E5" }}
              data-testid="window-warning"
            >
              <AlertTriangle size={16} color="#96650A" className="mt-0.5" />
              <div className="text-[13.5px]" style={{ color: "#96650A", lineHeight: 1.5 }}>
                That slot falls outside the service window for <strong>{selectedArea.name}</strong>.
                Review the right-hand panel for allowed hours.
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <Button className="h-10 px-5 bf-btn-primary" onClick={submit} disabled={submitting} data-testid="submit-btn">
              Create ticket
            </Button>
            <Button variant="outline" className="h-10 bf-btn-ghost" onClick={() => navigate("/dispatch")} data-testid="cancel-btn">Cancel</Button>
            <div className="ml-auto text-[12px]" style={{ color: "#5B6472" }}>
              Press <span className="kbd">Enter</span> on description to submit.
            </div>
          </div>
        </div>

        <aside className="bf-card" style={{ padding: "20px" }}>
          <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Context</div>
          <h3 className="text-[15px] font-semibold mt-1">{selectedArea?.name || "Select an area"}</h3>
          {selectedArea ? (
            <>
              <div className="mt-3 text-[12px]" style={{ color: "#5F6B7A" }}>Service windows (local AST):</div>
              <ul className="mt-1 space-y-1 text-[13px]">
                {selectedArea.service_windows?.map((w, i) => (
                  <li key={i} className="mono">
                    {w.start}–{w.end} · days {w.days.map(d => "MTWTFSS"[d]).join("")} · season {w.season_start}→{w.season_end}
                  </li>
                ))}
              </ul>
              {equipmentId && (() => {
                const eq = equipment.find(x => x.id === equipmentId);
                const parts = equipmentParts(eq);
                const cov = eq?.coverage_end_date && new Date(eq.coverage_end_date) >= new Date();
                return (
                  <div className="mt-4 p-3.5 rounded-[12px]" style={{ background: "#F1F3F6" }}>
                    <div className="text-[12px]" style={{ color: "#5B6472" }}>Selected equipment</div>
                    <div className="font-semibold mt-0.5 text-[15px]" data-testid="selected-equipment-type">{parts.type}</div>
                    {parts.makeModel && <div className="text-[13px]" data-testid="selected-equipment-make-model">{parts.makeModel}</div>}
                    {parts.serial && <div className="mono text-[12px]" style={{ color: "#5B6472" }} data-testid="selected-equipment-serial">SN {parts.serial}</div>}
                    <div className="mt-2 text-[13px]">
                      Coverage:
                      <span className="ml-1 font-medium" style={{ color: cov ? "#136B36" : "#B42318" }}>
                        {eq?.coverage_type || "None"} {eq?.coverage_end_date ? `· ends ${eq.coverage_end_date}` : ""}
                      </span>
                    </div>
                    {cov && <div className="mt-1 text-[12px]" style={{ color: "#136B36" }}>Parts should be covered — do not charge.</div>}
                  </div>
                );
              })()}
            </>
          ) : (
            <p className="text-[13px] mt-2" style={{ color: "#5F6B7A" }}>Pick customer → site → area to see service windows and coverage flags.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
        {label} {required && <span style={{ color: "#B42318" }}>*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
